import SimpleSearchBar from './SimpleSearchBar.jsx';
import styles from './AutocompleteSearchBox.scss';
import RenderInBody from '../RenderInBody.jsx';
import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import HighlightedText from './HighlightedText.jsx';
import PromiseDelayer from '../PromiseDelayer.js';
import Spinner from '../Spinner.jsx';
import classnames from 'classnames';
import {getPassthroughProps} from '../componentHelpers.js';
import AutocompleteSuggestion from './AutocompleteSuggestion.jsx';
import AutocompleteHeader from './AutocompleteHeader.jsx';

function defaultSuggestionRender(idx, props, item) {
  return <AutocompleteSuggestion key={idx} {...props} suggestion={item.suggestion} />;
}

class AutocompleteSearchBox extends React.Component {

  constructor(props) {
    super(props);

    this.handleClickOutside = this.handleClickOutside.bind(this);

    this.debouncer = new PromiseDelayer(250);

    this.state = {
      resultsLoading: false,
      autocompleteList: [],
      shouldShowResults: false,
      searchTerm: props.value
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    this.searchboxRect = this.getSearchBoxRect();
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (this.suggestions && !this.suggestions.contains(event.target)) {
      this.setState({shouldShowResults: false});
    }
  }

  performAutocomplete = async (term) => {
    this.setState({
      shouldShowResults: true,
      resultsLoading: true
    });

    let {autocomplete} = this.props;
    let autocompleteList = term ? await autocomplete(term) : [];

    autocompleteList = autocompleteList.map(suggestion => {
      let sug = suggestion;
      if (typeof suggestion === 'string' || suggestion instanceof String) {
        sug = {
          suggestion: suggestion
        };
      }
      return {
        focused: false,
        focusable: true,
        render: defaultSuggestionRender,
        ...sug
      };
    });

    this.setState({
      resultsLoading: false,
      autocompleteList
    });
  }

  valueChanged = async (newValue, immediate = false) => {
    this.setState({
      searchTerm: newValue
    });

    if (immediate) {
      this.debouncer.cancel();
      await this.performAutocomplete(newValue);
    }
    else {
      try {
        await this.debouncer.run(() => this.performAutocomplete(newValue));
      } catch (e) {
        await this.debouncer.swallowOwnRejections(e);
      }
    }
  }

  search = (searchTerm) => {
    this.debouncer.cancel();
    let {onSearch} = this.props;

    this.setState({
      searchTerm,
      shouldShowResults: false,
      autocompleteList: []
    });

    onSearch(searchTerm);
  }

  getSearchBoxRect = () => {
    if (this.searchbox) {
      let node = ReactDOM.findDOMNode(this.searchbox);
      if (node) {
        let rect = node.getBoundingClientRect();
        return {
          left: rect.left + window.pageXOffset,
          top: rect.top + window.pageYOffset,
          right: rect.right + window.pageXOffset,
          bottom: rect.bottom + window.pageYOffset,
          width: rect.width || (rect.right - rect.left),
          height: rect.height || (rect.bottom - rect.top)
        };
      }
    }
  }

  handleKeyPress = (event) => {
    if (event.key) {
      if (event.key.toLowerCase() === 'enter') {
        event.preventDefault();
        let searchTerm = this.state.searchTerm;
        let suggestion = this.state.autocompleteList.find(sug => sug.focused);
        if (suggestion && suggestion.focusSource === 'keyPress') {
          searchTerm = suggestion.suggestion;
          this.props.onSelectSuggestion(suggestion.suggestion);
        }
        this.search(searchTerm);
      }
      if (event.key.toLowerCase() === 'escape') {
        event.preventDefault();
        this.setState({
          shouldShowResults: false,
          autocompleteList: this.state.autocompleteList.map(s => {return {...s, focused: false};})
        });
      }
      if (event.key.toLowerCase() === 'arrowup' && this.state.autocompleteList.length) {
        event.preventDefault();
        let idx = this.state.autocompleteList.findIndex(suggestion => suggestion.focused);
        this.updateFocus(idx, "up", "keyPress");
      }
      if (event.key.toLowerCase() === 'arrowdown' && this.state.autocompleteList.length) {
        event.preventDefault();
        let idx = this.state.autocompleteList.findIndex(suggestion => suggestion.focused);
        this.updateFocus(idx, "down", "keyPress");
      }
    }
  }

  updateFocus = (idx, direction, focusSource) => {
    let i = idx;
    if (direction === "up") {
      do {
        i--;
        if (i < 0) {
          i = this.state.autocompleteList.length - 1;
        }
      } while (!this.state.autocompleteList[i].focusable);
    }
    else if (direction === "down") {
      do {
        i++;
        if (i === this.state.autocompleteList.length) {
          i = 0;
        }
      } while (!this.state.autocompleteList[i].focusable);
    }
    let autocompleteList = this.state.autocompleteList.map(suggestion => {return {...suggestion, focused: false, focusSource: null};});
    autocompleteList[i] = {...(autocompleteList[i]), focused: direction !== 'unset', focusSource: direction !== 'unset' ? focusSource : null};
    this.setState({autocompleteList, shouldShowResults:true});
  }

  render() {
    let {
      onCancel,
      style,
    } = this.props;
    return (
      <div style={style}>
        {
          this.renderSuggestions()
        }
        <SimpleSearchBar
          ref={(searchbox) => {
            this.searchbox = searchbox;
            this.searchboxRect = this.getSearchBoxRect();
          }}
          value={this.state.searchTerm}
          onChange={this.valueChanged}
          onSearch={() => this.search(this.state.searchTerm)}
          onClear={() => {
            this.valueChanged('', true);
            this.search('');
            onCancel();
          }}
          onKeyDown={e => this.handleKeyPress(e)}
          {...getPassthroughProps(this)}
        />
      </div>
    );
  }

  renderSuggestions() {
    if ((!this.state.shouldShowResults || !this.state.searchTerm) && !this.state.resultsLoading) {
      return null;
    }
    let dropdownStyle = {};
    if (this.searchboxRect) {
      dropdownStyle = {
        position: 'absolute',
        top: this.searchboxRect.height,
        left: 0,
        width: this.searchboxRect.width
      };
    }

    return (
      <div style={{position: 'relative', display:'inline-block'}}>
        <div ref={suggestions => this.suggestions = suggestions} style={dropdownStyle} className={styles.suggestions}>
          <div className={styles.searchNow}>Search for "{this.state.searchTerm}"</div>
          {
            this.state.resultsLoading
              ? (
                <div className={styles.spinner}>
                  <hr />
                  <div className={styles.iconContainer}>
                    <Spinner className={`warning ${styles.larger}`} />
                  </div>
                </div>
              )
              :
              this.state.autocompleteList.length > 0
                ?
                this.state.autocompleteList.map((item, idx) => {
                  let props = {
                    onClick: () => {
                      this.search(item.suggestion);
                      this.props.onSelectSuggestion(item.suggestion);
                    },
                    focused: item.focused,
                    onMouseEnter: () => this.updateFocus(idx, null, "mouseEnter"),
                    onMouseLeave: () => this.updateFocus(idx, 'unset'),
                    searchTerm: this.state.searchTerm
                  };
                  return item.render(idx, props, item);
                })
                :
                <div className={styles.spinner}>
                  <hr />
                  <div style={{paddingTop: '12px'}}>No results found for your search. Please try another.</div>
                </div>
          }
        </div>
      </div>
    );
  }
}

AutocompleteSearchBox.propTypes = {
  value: PropTypes.string,
  onCancel: PropTypes.func,
  onSearch: PropTypes.func.isRequired,
  onSelectSuggestion: PropTypes.func,
  autocomplete: PropTypes.func,
  style: PropTypes.object
};

AutocompleteSearchBox.defaultProps = {
  autocomplete: () => [],
  onCancel: () => {},
  onSelectSuggestion: () => {},
  style: {},
  value: '',
};

export default AutocompleteSearchBox;